/**
 * The three paint ramps a game mark can be drawn in.
 *
 * One hidden `<svg>` of `<defs>`, mounted once for the whole app. The icons
 * themselves are unchanged — they still say `fill="currentColor"`, and the
 * `.tone-*` rules in `styles.css` point that paint at one of these instead of
 * at a flat color.
 *
 * ## Why the definitions are here and not in each icon
 *
 * `Icon` inlines its source, so the six marks in the Popular Games strip are
 * six copies of the same markup in one document. A gradient carries an `id`,
 * ids are document-wide, and six copies of `id="mark-gold"` is one winner and
 * five duplicates — every icon on the page would quietly resolve to whichever
 * copy React happened to mount first. Defining them once, outside the icons,
 * is the only arrangement where that question does not arise.
 *
 * ## The details that make it read as lit rather than blotchy
 *
 * - **`gradientUnits="userSpaceOnUse"`.** The default, `objectBoundingBox`,
 *   restarts the ramp inside *every* path it paints, so the crown's base bar
 *   would get its own full light-to-dark pass and the two cards in a pair
 *   would each be lit from their own top edge. In user space all of them share
 *   one ramp down the icon, which is what a single light source looks like.
 * - **y1=2 to y2=22 rather than 0 to 24.** The glyphs are drawn inside that
 *   band, so the ends of the ramp land on the art instead of being spent in
 *   the viewBox margin.
 * - **Stops as `stopColor` style rather than `stop-color` attributes.** A
 *   presentation attribute cannot hold a `var()`, and these ramps are the
 *   palette in `styles.css` — the gold one is the same bevel the wordmark is
 *   drawn with, so a mark is lit the way the logo is.
 */

/** Ramp geometry, shared by all three: straight down the drawn area. */
const DOWN = { gradientUnits: 'userSpaceOnUse', x1: 12, y1: 2, x2: 12, y2: 22 } as const;

export function MarkGradients() {
  return (
    /*
     * Not `display: none`. A hidden subtree still has to be *rendered* for
     * paint servers inside it to resolve; the CSS takes it out of layout
     * instead (see `.mark-gradients`). `focusable` is for IE-era SVG focus
     * behavior that Edge inherited, and costs nothing to keep off.
     */
    <svg className="mark-gradients" aria-hidden="true" focusable="false">
      <defs>
        {/* Brand gold, lit from above: bevel, body, and the deep tone held
            back to the last few percent so it reads as weight at the base
            rather than as mud across the bottom half. */}
        <linearGradient id="mark-gold" {...DOWN}>
          <stop offset="0" style={{ stopColor: 'var(--gold-bevel)' }} />
          <stop offset="0.42" style={{ stopColor: 'var(--accent)' }} />
          <stop offset="0.82" style={{ stopColor: 'var(--gold-body)' }} />
          <stop offset="1" style={{ stopColor: 'var(--gold-deep)' }} />
        </linearGradient>

        {/* Hearts and diamonds. Kept clearly red at every stop: a red suit
            that drifts orange at the top is the kind of small wrongness a
            card player feels without being able to name it. */}
        <linearGradient id="mark-red" {...DOWN}>
          <stop offset="0" style={{ stopColor: 'var(--suit-red-lift)' }} />
          <stop offset="0.5" style={{ stopColor: 'var(--suit-red)' }} />
          <stop offset="1" style={{ stopColor: 'var(--suit-red-deep)' }} />
        </linearGradient>

        {/* Spades and clubs, which are paper-white here rather than black
            because they sit on felt. The ramp is cool, not grey-brown, so it
            stays on the green side of neutral against the table. */}
        <linearGradient id="mark-dark" {...DOWN}>
          <stop offset="0" style={{ stopColor: '#ffffff' }} />
          <stop offset="0.45" style={{ stopColor: 'var(--paper)' }} />
          <stop offset="1" style={{ stopColor: 'var(--paper-shade)' }} />
        </linearGradient>
      </defs>
    </svg>
  );
}
